import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetClients } from "@/hooks/clients";
import { ReservationClient } from "@/models";
import {
  CreatePaymentAdjustment,
  PaymentAdjustmentReservationClient,
  PaymentAdjustmentTypesAsArray,
} from "@/models/payment-adjustment";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function Form({
  model,
  onUpsert,
  reservationId,
  reservationClients,
}: {
  reservationId: string;
  reservationClients: ReservationClient[];
  model?: CreatePaymentAdjustment;
  onUpsert: (data: CreatePaymentAdjustment) => Promise<void>;
}) {
  const clientsIds = reservationClients.map((rC) => rC.clientId.toString());

  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreatePaymentAdjustment>>(() => {
    const config: FormOptions<CreatePaymentAdjustment> = {
      comissions: {
        value: model?.comissions,
      },
      markup: {
        value: model?.markup ?? 0,
      },
      pvp: {
        value: model?.pvp ?? 0,
      },
      paymentAdjustmentReservationClients: {
        value: model?.paymentAdjustmentReservationClients ?? [],
      },
      reservationId: {
        value: Number.parseInt(reservationId),
      },
      type: {
        value: model?.type,
      },
      discount: {
        value: model?.discount,
      },
      total: {
        value: model?.total ?? 0,
      },
    };
    return config;
  }, [model, reservationId]);

  const { data: clientsDetails, isLoading: clientsDetailsloading } =
    useGetClients();

  const recalculateTotal = (
    comissions: number,
    pvp: number,
    markup: number,
    discount: number,
    qt: number,
  ) => {
    const comissionVal = (comissions ?? 0) / 100;
    const leftHand = pvp * (1 - comissionVal) + (markup ?? 0);
    const discountVal = (discount ?? 0) / 100;
    return leftHand * qt * (1 - discountVal);
  };

  return (
    <DomainUpsert
      title={model?.type ?? t("paymentAdjustments.new.title")}
      buttonTranslation={
        isEdit
          ? "paymentAdjustments.update.button"
          : "paymentAdjustments.new.button"
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "type",
          label: "paymentAdjustments.fields.type",
          input: (form, id) => (
            <SelectSearch
              id={id}
              className="w-full"
              value={form.value.type}
              onChange={(v) => {
                form.setValue("type", v);
              }}
              options={
                PaymentAdjustmentTypesAsArray?.map((v) => ({
                  value: v,
                  label: t(`paymentAdjustments.types.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "pvp",
          label: "paymentAdjustments.fields.pvp",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              min="0"
              inputMode="decimal"
              value={form.value.pvp ?? ""}
              onChange={(e) => {
                const localV = parseInt(e.target.value);
                const tt = recalculateTotal(
                  form.value.comissions ?? 0,
                  localV ?? 0,
                  form.value.markup ?? 0,
                  form.value.discount ?? 0,
                  form.value.paymentAdjustmentReservationClients?.length ?? 0,
                );
                form.setValue("pvp", localV);

                form.setValue("total", tt);
              }}
            />
          ),
        },
        {
          key: "comissions",
          label: "paymentAdjustments.fields.comissions",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              value={form.value.comissions ?? ""}
              onChange={(e) => {
                const localV = parseInt(e.target.value);
                const tt = recalculateTotal(
                  localV ?? 0,
                  form.value.pvp ?? 0,
                  form.value.markup ?? 0,
                  form.value.discount ?? 0,
                  form.value.paymentAdjustmentReservationClients?.length ?? 0,
                );
                form.setValue("comissions", localV);
                form.setValue("total", tt);
              }}
            />
          ),
        },
        {
          key: "markup",

          label: "paymentAdjustments.fields.markup",
          input: (form, id) => (
            <Input
              className="w-full"
              id={id}
              type="number"
              inputMode="decimal"
              onChange={(v) => {
                const localV = parseInt(v.target.value);

                const tt = recalculateTotal(
                  form.value.comissions ?? 0,
                  form.value.pvp ?? 0,
                  localV ?? 0,
                  form.value.discount ?? 0,
                  form.value.paymentAdjustmentReservationClients?.length ?? 0,
                );
                form.setValue("markup", localV);

                form.setValue("total", tt);
              }}
              value={form.value.markup ?? {}}
            />
          ),
        },
        {
          key: "paymentAdjustmentReservationClients",
          label: "paymentAdjustments.fields.client",
          input: (form, id) =>
            clientsDetailsloading ? (
              <Skeleton />
            ) : (
              <SelectSearch
                id={id}
                value={
                  form.value.paymentAdjustmentReservationClients?.[0]?.clientId
                }
                className="w-ful"
                onChange={(v) => {
                  const tt = recalculateTotal(
                    form.value.comissions ?? 0,
                    form.value.pvp ?? 0,
                    form.value.markup ?? 0,
                    form.value.discount ?? 0,
                    v ? 1 : 0,
                  );

                  const localV: PaymentAdjustmentReservationClient[] =
                    clientsDetails
                      ?.filter((c) => c.id === v)
                      .map((c) => ({
                        clientId: c.id,
                        paymentAdjustmentId: "",
                        reservationId: reservationId,
                        //client: c,
                      })) ?? [];

                  form.setValue("paymentAdjustmentReservationClients", [
                    ...localV,
                  ]);

                  form.setValue("total", tt);
                }}
                options={
                  clientsDetails
                    ?.filter((c) => clientsIds.includes(c.id))
                    ?.map((v) => ({
                      value: v.id,
                      label: v.name,
                    })) ?? []
                }
              />
            ),
        },
        {
          key: "discount",
          label: "paymentAdjustments.fields.discount",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              max="100"
              min="0"
              value={form.value.discount ?? ""}
              onChange={(e) => {
                const localV = parseInt(e.target.value);
                const tt = recalculateTotal(
                  form.value.comissions ?? 0,
                  form.value.pvp ?? 0,
                  form.value.markup ?? 0,
                  localV ?? 0,
                  form.value.paymentAdjustmentReservationClients?.length ?? 0,
                );
                form.setValue("discount", localV);

                form.setValue("total", tt);
              }}
            />
          ),
        },
        {
          key: "total",
          label: "paymentAdjustments.fields.total",
          input: (form, id) => {
            return <span key={id}>{form.value.total ?? 0}</span>;
          },
        },
      ]}
    />
  );
}
